// --- Dependencies -------------------------------------------------------------------------------
import type { KeyboardEvent, MouseEvent } from "react";
// --- Logic --------------------------------------------------------------------------------------
/**
 * A card opens from anywhere on it, so the whole card carries the click. It cannot be a `<button>` —
 * it holds its own more-actions button, and a button inside a button is invalid — so this gives it
 * the keyboard half of the same deal: Enter and Space, the two keys a button answers to.
 *
 * The target check is what keeps those keys from opening the card when the press was meant for a
 * control inside it; a click does the same with `stopPropagation` at the control.
 */
export function pressToOpen(onOpen: () => void) {
    return (event: KeyboardEvent<HTMLElement>) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        if (event.target !== event.currentTarget) return;
        event.preventDefault();
        onOpen();
    };
}

/**
 * The same more-actions menu, opened by the right button and hung off the pointer itself.
 *
 * A card's menu button is a small target in the corner of a row, and the gesture everybody already
 * knows for "what can I do with this" is a right-click. The anchor is the click point rather than
 * the card, because a menu that opened over the far corner of a wide card would look like it
 * belonged to something else — `PopupPaper` measures from the rect it is given and flips it near an
 * edge, so a zero-sized rect at the cursor is all it needs.
 *
 * The browser's own menu is suppressed only where we put one in its place.
 */
export function rightClickToOpen(onActions: (anchor: DOMRect) => void) {
    return (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onActions(new DOMRect(event.clientX, event.clientY, 0, 0));
    };
}
